import { createSignal, createEffect, Show, batch } from "solid-js";
import { Portal } from "solid-js/web";
import { FiSettings } from "solid-icons/fi";
import { persistantStore } from "../../store/persistantStore";
import { sourcesStore } from "../../store/sourcesStore";
import { debug } from "../../utils/console";
import MetadataFilterFields, { resolveStringAccessor } from "./MetadataFilterFields";
import CheatSheetStateFilter from "./CheatSheetStateFilter";
import { normalizeCheatSheetStateTokens } from "../../utils/metadataFilterTokens";

/**
 * Cheat Sheet page-settings menu: a gear icon that opens a modal with Year and Event
 * contains-filters (same UX + MetadataPicker browse as Performance History's PerfSettings,
 * but scoped to the two filters the cheat sheet supports). Config is handled by the page's
 * existing Configuration dropdown, so it is intentionally not duplicated here.
 * Used by the source-variant cheat sheet; the fleet variant folds the same fields into
 * its existing RaceSettings modal.
 */
interface CheatSheetSettingsProps {
  filterYear?: string | (() => string);
  filterEvent?: string | (() => string);
  filterState?: string | (() => string);
  onFilterYearChange?: (year: string) => void;
  onFilterEventChange?: (event: string) => void;
  onFilterStateChange?: (state: string) => void;
}

export default function CheatSheetSettings(props: CheatSheetSettingsProps) {
  const [showPopup, setShowPopup] = createSignal(false);
  const [filterYearValue, setFilterYearValue] = createSignal<string>("");
  const [filterEventValue, setFilterEventValue] = createSignal<string>("");
  const [filterStateValue, setFilterStateValue] = createSignal<string>("H0");

  // Seed local mirrors from the parent, and keep them in sync while the modal is closed
  // (e.g. share-link restore). Reads happen inside a tracked scope to stay reactive.
  createEffect(() => {
    const parentYear = resolveStringAccessor(props.filterYear);
    if (!showPopup()) setFilterYearValue(parentYear);
  });
  createEffect(() => {
    const parentEvent = resolveStringAccessor(props.filterEvent);
    if (!showPopup()) setFilterEventValue(parentEvent);
  });
  createEffect(() => {
    // Normalize/repair to the canonical H0/H1/H2 set (defaults to H0).
    const parentState = normalizeCheatSheetStateTokens(resolveStringAccessor(props.filterState)).join(",");
    if (!showPopup()) setFilterStateValue(parentState);
  });

  /** Lowercased source names for the MetadataPicker: the selected source, else all project sources. */
  const getMetadataSourceNamesLower = (): string[] => {
    const sid = persistantStore.selectedSourceId?.();
    if (sid != null && Number(sid) > 0) {
      const name = sourcesStore.getSourceName(Number(sid));
      if (name) return [String(name).trim().toLowerCase()];
    }
    const all = sourcesStore.sources?.() ?? [];
    return all.map((s) => String(s.source_name).trim().toLowerCase()).filter((n) => n.length > 0);
  };

  const applyFilters = () => {
    // Batch so the page's fetch effects (which read the filters) flush once, not per write
    batch(() => {
      props.onFilterYearChange?.(filterYearValue().trim());
      props.onFilterEventChange?.(filterEventValue().trim());
      props.onFilterStateChange?.(normalizeCheatSheetStateTokens(filterStateValue()).join(","));
    });
    setShowPopup(false);
    debug("CheatSheetSettings: applied filters", {
      year: filterYearValue(),
      event: filterEventValue(),
      state: filterStateValue(),
    });
  };

  const clearFilters = () => {
    setFilterYearValue("");
    setFilterEventValue("");
    // State can never be empty — clearing resets to the H0-only default.
    setFilterStateValue("H0");
  };

  return (
    <>
      <div class="relative inline-block settings-parent">
        <div
          onClick={() => setShowPopup(!showPopup())}
          onContextMenu={(e) => e.preventDefault()}
          class="performance-settings-icon cursor-pointer p-1 hover:opacity-70 transition-opacity"
          title="Page settings"
        >
          <FiSettings size={24} />
        </div>
      </div>

      <Show when={showPopup()}>
        <Portal mount={typeof document !== "undefined" ? (document.getElementById("main-content") || document.body) : undefined}>
          <div class="pagesettings-overlay" onClick={() => setShowPopup(false)}>
            <div class="pagesettings-modal" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div class="flex justify-between items-center p-4 border-b" style="border-color: var(--color-border-primary);">
                <h2 class="text-lg font-semibold" style="color: var(--color-text-primary);">Page Settings</h2>
                <button
                  onClick={() => setShowPopup(false)}
                  class="text-gray-500 hover:text-gray-700 transition-colors"
                  style="color: var(--color-text-secondary);"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div class="p-6">
                <h3 class="text-base font-semibold mb-3" style="color: var(--color-text-primary);">Filters</h3>
                <MetadataFilterFields
                  yearValue={filterYearValue}
                  onYearInput={setFilterYearValue}
                  eventValue={filterEventValue}
                  onEventInput={setFilterEventValue}
                  getSourceNames={getMetadataSourceNamesLower}
                />
                <div class="mt-4">
                  <CheatSheetStateFilter value={filterStateValue} onChange={setFilterStateValue} />
                </div>
                <p class="text-xs mt-2" style="color: var(--color-text-secondary);">
                  Filters apply to all three cheat sheet tables. Leave Year / Event blank to include every
                  year / event. Foiling State always keeps at least H0 — maneuvers whose state contains any
                  selected state (e.g. H0-H1) are included.
                </p>
              </div>

              {/* Footer */}
              <div class="flex justify-end gap-3 p-4 border-t" style="border-color: var(--color-border-primary);">
                <button
                  type="button"
                  onClick={clearFilters}
                  class="px-4 py-2 rounded-md text-sm"
                  style="background: var(--color-bg-button-secondary); color: var(--color-text-button-secondary);"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={applyFilters}
                  class="px-4 py-2 rounded-md text-sm font-semibold"
                  style="background: #10b981; color: #ffffff;"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </Portal>
      </Show>
    </>
  );
}
